import React, {
  FunctionComponent,
  useState,
  useEffect,
  useCallback,
  ChangeEvent,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Icon, IconColor, IconName } from 'app/common/components/controls';
import { featureFlagClient } from '@trello/feature-flag-client';
import { forTemplate, forNamespace } from 'app/common/lib/util/i18n';
import { Button } from '@trello/nachos';
import {
  PopoverMenu,
  PopoverMenuButton,
} from 'app/gamma/src/components/popover/popover-menu';

import styles from './channel-switcher.less';

const format = forTemplate('header_channel_picker');
const viewTitle = forNamespace('view title');

export const FeatureFlagList: FunctionComponent = () => {
  const [flags, setFlags] = useState(featureFlagClient.all());
  const [query, setQuery] = useState('');

  const onFlagChange = useCallback(() => {
    setFlags(featureFlagClient.all());
  }, []);

  const flagToggleHandler = useCallback(
    (key: string, value: boolean) => {
      featureFlagClient.setOverride(key, value);
      onFlagChange();
    },
    [onFlagChange],
  );

  const resetOverridesHandler = useCallback(() => {
    featureFlagClient.resetOverrides();
    onFlagChange();
  }, [onFlagChange]);

  useEffect(() => {
    featureFlagClient.client.on('change', onFlagChange);
    return () => {
      featureFlagClient.client.off('change', onFlagChange);
    };
  }, [onFlagChange]);

  const onQueryChanged = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  }, []);

  const sortedFlags = useMemo(
    () =>
      Object.entries({ ...flags.remote, ...flags.overrides })
        .filter(
          ([key, value]) => key.includes(query) && typeof value === 'boolean',
        )
        .sort(),
    [flags.overrides, flags.remote, query],
  );

  return (
    <div>
      <p>{format('listed-below-are-feature-flags')}</p>
      <Button
        appearance="danger"
        onClick={resetOverridesHandler}
        shouldFitContainer
      >
        {format('reset-all-feature-flags')}
      </Button>
      <hr />
      <div className={styles.searchWrapper}>
        <Icon name={IconName.Search} className={styles.searchIcon} small />
        <input
          autoFocus
          className={styles.searchInput}
          placeholder={viewTitle('feature flags')}
          value={query}
          onChange={onQueryChanged}
        />
      </div>
      <PopoverMenu className={styles.popoverMenu}>
        {sortedFlags.map(([key, value]) => {
          const hasOverride = flags.overrides[key] !== undefined;

          return (
            <PopoverMenuButton
              key={key}
              className={classNames(
                styles.featureFlagMenuButton,
                (value || hasOverride) && styles.featureFlagSelected,
              )}
              onClick={() => flagToggleHandler(key, !value)}
            >
              {value && (
                <Icon
                  small
                  name={IconName.Check}
                  color={hasOverride ? IconColor.Green : IconColor.Gray}
                />
              )}
              {flags.overrides[key] === false && (
                <Icon small name={IconName.Close} color={IconColor.Red} />
              )}
              {key}
            </PopoverMenuButton>
          );
        })}
      </PopoverMenu>
    </div>
  );
};
